import { SECRET_TYPES } from '@/types';

import { isSecurityKeyChallenge } from '@/utils/webauthn/webauthn';

import type {
    ApiResponse,
    EmailVerificationState,
    PinType,
    SecretDetails,
    SecretStatus,
    SecretType,
    UrlPinType,
    ViewSecretRequest,
} from '@/types';

// =============================================================================
// Type Guards
// =============================================================================

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isSecretType = (value: unknown): value is SecretType => {
    return typeof value === 'string' && (Object.values(SECRET_TYPES) as string[]).includes(value);
};

// =============================================================================
// PIN Type Normalization
// =============================================================================

export const normalizeUrlPinType = (pinType: UrlPinType | undefined): PinType | null => {
    if (!pinType) return null;

    const normalized = String(pinType).toLowerCase().trim();

    const pinTypeMap: Record<string, PinType> = {
        static: 'static',
        pin: 'static',
        dynamic: 'dynamic',
        dpin: 'dynamic',
        email: 'email',
        securitykey: 'securityKey',
        security_key: 'securityKey',
        'security-key': 'securityKey',
        'security key': 'securityKey',
    };

    return pinTypeMap[normalized] ?? 'static';
};

export const resolvePinTypeFromDetails = (details?: SecretDetails): PinType | null => {
    if (!details) return null;

    // Check email verification first
    const emailVerification = details.emailVerification;
    if (emailVerification === true) return 'email';
    if (isPlainObject(emailVerification) && emailVerification.id) return 'email';

    // Check security key (a pending challenge counts)
    const hasSecurityKey =
        isSecurityKeyChallenge(details) ||
        (Array.isArray(details.securityKey) && details.securityKey.length > 0) ||
        (Array.isArray(details.keys) && details.keys.length > 0);
    if (hasSecurityKey) return 'securityKey';

    // Check PIN object
    if (isPlainObject(details.pin)) {
        const pinObj = details.pin as { static?: string; dynamic?: string };
        if (pinObj.dynamic) return 'dynamic';
        if (pinObj.static) return 'static';
    }

    // Check PIN string
    if (typeof details.pin === 'string') {
        const pinLower = details.pin.toLowerCase();
        if (pinLower.includes('dynamic')) return 'dynamic';
        if (pinLower.includes('static')) return 'static';
    }

    return null;
};

// =============================================================================
// Email Verification Helpers
// =============================================================================

export const getEmailVerificationState = (details?: SecretDetails): EmailVerificationState => {
    // const details = { emailVerification: true, hint: 'ki**@s*******.c**' };

    if (!details) {
        return { hintEnabled: false };
    }

    const hintValue = details?.hint?.trim() || undefined;
    const emailVerification = details.emailVerification;

    if (!emailVerification) {
        return { hintEnabled: false };
    }

    const id =
        typeof emailVerification === 'object' && !!emailVerification
            ? (emailVerification as { id?: string }).id
            : undefined;

    return {
        id,
        hintEnabled: Boolean(hintValue),
        hintValue,
    };
};

export const isEmailVerificationRequired = (details?: SecretDetails): boolean => {
    if (!details?.emailVerification) return false;
    if (details.emailVerification === true) return true;
    return isPlainObject(details.emailVerification);
};

// =============================================================================
// Secret Type Normalization
// =============================================================================

export const normalizeSecretType = (value: unknown): SecretType => {
    if (isSecretType(value)) return value;
    return SECRET_TYPES.MESSAGE;
};

// =============================================================================
// API Response Parser
// =============================================================================
interface ParsedApiResult {
    status: SecretStatus;
    pinError?: string;
    message?: string;
    serverMessage?: string;
}

export const parseApiResponse = (
    response: ApiResponse<SecretDetails> | undefined,
    hasError: boolean
): ParsedApiResult => {
    if (!response) {
        return { status: 'error', serverMessage: 'Empty server response.' };
    }

    // Refusals come as HTTP errors (401/403/404) with the same { status, message } as any answer
    // (API Gateway's error template for pull-secret): they are read the same way, so "already opened"
    // or "expired" get their own screen instead of "Something went wrong". `hasError` only matters
    // when nothing below recognizes the answer.
    const apiStatus = String(response.status ?? '')
        .toLowerCase()
        .trim();

    const rawMessage = String(response.message ?? '').trim();
    const message = rawMessage.toLowerCase();

    const details = response.details;

    if (
        apiStatus === 'notfound' ||
        apiStatus === 'not_found' ||
        message.includes('not found') ||
        message.includes('id is not valid')
    ) {
        return {
            status: 'not_found',
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    if (apiStatus === 'expired' || message.includes('expired')) {
        return {
            status: 'expired',
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    // "Secret already retrieved and erased 3 hours ago." / "One-time link already used."
    if (
        message.includes('already retrieved') ||
        message.includes('already viewed') ||
        message.includes('already opened') ||
        message.includes('already used')
    ) {
        return {
            status: 'already_viewed',
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    // "Secret erased.": its sender deleted the content.
    if (message.includes('erased')) {
        return {
            status: 'erased',
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    // Locked by the owner, no PIN attempts left, or not open yet: the server's words say which.
    if (
        message.includes('locked by the owner') ||
        message.includes('attempts already exhausted') ||
        message.includes('will be available in')
    ) {
        return {
            status: 'unavailable',
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    if (message.includes('security key')) {
        return {
            status: 'pin_required',
            pinError: 'The security key could not be verified. Please try again with an allowed key.',
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    // A wrong PIN or code keeps the reader on the challenge, with the server's own words: they say how many
    // tries are left ("PIN number not valid. Attempts remaining: 1", "Email verification code not valid.
    // Attempts remaining: 2"). Matching only "invalid pin" / "invalid code" let these fall through to the
    // generic error and out of the flow while tries remained.
    const aboutCode = message.includes('code');
    const aboutPin = message.includes('pin');
    const refused =
        message.includes('not valid') ||
        message.includes('invalid') ||
        message.includes('wrong') ||
        message.includes('incorrect') ||
        message.includes('attempts remaining');

    if ((aboutCode || aboutPin) && refused) {
        const emailCode = aboutCode && (message.includes('email') || isEmailVerificationRequired(details));
        return {
            status: emailCode ? 'pin_email_code' : 'pin_required',
            pinError: rawMessage || (emailCode ? 'Invalid code. Please try again.' : 'Invalid PIN. Please try again.'),
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    // "Code already sent to authorized email. Retry again later.": the code is on its way; the reader
    // waits on the code step rather than starting over.
    if (
        message.includes('code sent') ||
        message.includes('verification code sent') ||
        message.includes('already sent')
    ) {
        return {
            status: 'pin_email_code',
            ...(message.includes('already sent') ? { pinError: rawMessage } : {}),
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    if (message.includes('secret is pin protected') && apiStatus === 'unauthorized') {
        return {
            status: 'pin_required',
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    // "Email not valid. Attempts remaining: 1": the reader mistyped the address. The answer carries
    // no details, so the generic branch below read it as a failure and hid the one thing worth
    // saying — that the address was wrong, and how many tries are left.
    if (message.includes('email not valid') || message.includes('email is not valid')) {
        return {
            status: 'pin_email_guess',
            pinError: rawMessage,
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    // The captcha the e-mail step needs could not be checked; asking again is what helps.
    if (message.includes('captcha')) {
        return {
            status: 'pin_email_guess',
            pinError: 'Could not verify you are human. Please try again.',
            serverMessage: rawMessage,
            message: rawMessage,
        };
    }

    const isUnauthorized =
        apiStatus === 'unauthorized' || apiStatus === 'forbidden' || message.includes('unauthorized');

    if (isUnauthorized) {
        const hasEmail = isEmailVerificationRequired(details);

        return {
            status: hasEmail ? 'pin_email_guess' : 'error',
            serverMessage: rawMessage || 'Unauthorized',
            message: rawMessage || 'Unauthorized',
        };
    }

    // An error answer none of the above recognized carries no secret to look for.
    if (hasError) {
        return {
            status: 'error',
            serverMessage: rawMessage || 'Request failed.',
            message: rawMessage || 'Request failed.',
        };
    }

    const hasContent = Boolean(details?.secret?.trim());
    const hasDownload = Boolean(details?.download);
    const secretType = normalizeSecretType(details?.type);

    if (apiStatus === 'success') {
        if (isSecurityKeyChallenge(details)) {
            return {
                status: 'pin_required',
                serverMessage: rawMessage || undefined,
                message: rawMessage || undefined,
            };
        }

        if (isEmailVerificationRequired(details) && !hasContent && !hasDownload) {
            return {
                status: 'pin_email_guess',
                serverMessage: rawMessage || undefined,
                message: rawMessage || undefined,
            };
        }

        if ((secretType === SECRET_TYPES.FILE && hasDownload) || hasContent) {
            return {
                status: 'ready',
                serverMessage: rawMessage || undefined,
                message: rawMessage || undefined,
            };
        }

        const hasPinProtection =
            Boolean(details?.pin) ||
            (Array.isArray(details?.securityKey) && details.securityKey.length > 0) ||
            (Array.isArray(details?.keys) && details.keys.length > 0);

        if (hasPinProtection) {
            return {
                status: 'pin_required',
                serverMessage: rawMessage || undefined,
                message: rawMessage || undefined,
            };
        }
    }

    if ((secretType === SECRET_TYPES.FILE && hasDownload) || hasContent) {
        return {
            status: 'ready',
            serverMessage: rawMessage || undefined,
            message: rawMessage || undefined,
        };
    }

    if (isEmailVerificationRequired(details)) {
        return {
            status: 'pin_email_guess',
            serverMessage: rawMessage || undefined,
            message: rawMessage || undefined,
        };
    }

    return {
        status: 'error',
        serverMessage: rawMessage || 'Unknown server error.',
        message: rawMessage || 'Unknown server error.',
    };
};
// =============================================================================
// Request Builder
// =============================================================================

export const buildViewSecretRequest = (
    secretId: string,
    pinType: PinType | null,
    pinValue?: string,
    emailGuessValue?: string
): ViewSecretRequest => {
    const base = { secretId };

    if (pinType === 'email') {
        if (emailGuessValue) return { ...base, emailGuess: emailGuessValue };
        if (pinValue) return { ...base, emailVerificationCode: pinValue };
        return base;
    }

    if (!pinValue) return base;

    if (pinType === 'securityKey') {
        return { ...base, challengeresponse: pinValue };
    }

    return { ...base, pin: pinValue, emailVerificationCode: pinValue };
};
